import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

const LINK = 'text-brand-300 hover:text-brand-200 underline underline-offset-2';

function PageParty({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Party</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Party mode lets 2 to 4 clients on this PC farm as a team. Each client still runs its own
        route, but they wait for each other before leaving a map and at bloodshed points, they can
        start and stop together, and they can resurrect each other. Key Farm uses the same
        connection. Everything is on the{' '}
        <strong className="text-white">AUTOPILOT &rsaquo; Party</strong> tab: settings on the left in
        the <strong className="text-white">PARTY</strong> card, the live picture on the right in the{' '}
        <strong className="text-white">TEAM</strong> card.
      </p>

      <Callout type="info">
        <strong>No distances to set up.</strong> Each client says for itself when it has reached a
        meeting point on its own route. Your routes do not need matching positions &mdash; only the
        same maps to go to.
      </Callout>

      <DocHeading>Setting It Up</DocHeading>
      <Step n={1} title="Start 2 to 4 clients on this PC">
        Log in the characters and put them in one in-game group. Resurrecting a teammate and the group
        bonus both work through the game&rsquo;s group.
      </Step>
      <Step n={2} title="Load a route on each client">
        Each client runs its own route on{' '}
        <button onClick={() => navigate('autopilot')} className={LINK}>AUTOPILOT &rsaquo; Play</button>.
        The routes must leave each map to the same next map. Ideally they also have the same number of
        bloodshed points per map, so the team opens the chests together.
      </Step>
      <Step n={3} title="Turn on Play as a party in every client">
        It is off by default and it is per client. There is nothing else to match between them.
      </Step>
      <Step n={4} title="Check the TEAM card">
        Under <strong>Teammates</strong> every other client should appear, named like{' '}
        <em>client 2</em>. If it says <em>Nobody right now.</em>, see Quick Troubleshooting.
      </Step>
      <Step n={5} title="Press START on one client">
        With <strong>Start and stop together</strong> on, the others start the route they have loaded.
      </Step>

      <Shot
        name="party-01-tab.png"
        tab="AUTOPILOT › Party"
        caption="Two clients farming as a party: the teammate is here, 16 m away, with the group bonus active; one resurrect taken, two sent."
        shoot="menushot (stage party + pairchan team): az AUTOPILOT › Party lap — PARTY (Play as a party, Start and stop together, Patience 30 s, a két resurrect kapcsoló BE, ADVANCED csukva); jobbra TEAM (This client (slot 0), Doing working - map change 12, Teammates: Second client here, 16 m, group bonus active; Resurrect: Taken 1, Sent 2)."
      />

      <DocHeading>The PARTY Card</DocHeading>
      <DocTable
        headers={['Setting', 'What it does', 'Suggested']}
        rows={[
          [<strong className="text-white">Play as a party</strong>, 'The main switch, one per client. Off means this client works alone. Start and stop together, Patience, Resurrect dead teammates and ADVANCED appear once it is on.', 'On in every client'],
          [<strong className="text-white">Start and stop together</strong>, 'START on one client starts the route the others already have loaded. STOP works the same way, and it also fires when a client’s run ends on its own. A client that is already running is left alone.', 'On'],
          [<strong className="text-white">Patience</strong>, 'Only used when the team cannot come together: someone waits at an exit to another map, or is dead and nobody revives them. After this time the others go on; the one left behind rejoins when it catches up. 5–180 s.', '30 s'],
          [<strong className="text-white">Accept resurrect offers</strong>, 'Takes a teammate’s offer to resurrect you. Costs you nothing. While a teammate close by can offer, your own Auto Revive waits a few seconds for it. Only does anything while Play as a party is on.', 'On'],
          [<strong className="text-white">Resurrect dead teammates</strong>, 'Offers a resurrect to a dead teammate. Spends a spiritstone every time. Only when the dead one is close, one offer per death, and only one teammate offers.', 'On where you have stones'],
          [<strong className="text-white">ADVANCED &rsaquo; Bloodshed: wait for the group bonus</strong>, 'Before every chest, waits until the group bonus is active on each teammate at the bloodshed point — the same thing the glowing ring in the game’s group window shows. If it does not turn on within Patience, the chest is opened anyway.', 'Off, unless the bonus matters to you'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        Hover the small round marker next to a setting to read the same explanation in the menu.
        While Play as a party is on, the AUTOPILOT tab shows a green dot.
      </p>

      <DocHeading>Where the Team Waits</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        There are four moments when a client holds back for the others. While it waits, the character
        still defends itself, but it does not run after anything. The status line on{' '}
        <strong className="text-white">AUTOPILOT &rsaquo; Play</strong> says what it is waiting for.
      </p>
      <DocTable
        headers={['Moment', 'What happens', 'Play tab says']}
        rows={[
          [<strong className="text-white">Starting the run</strong>, 'If the route begins with a jump from town, nobody starts walking until everyone who started is on the same map and has loaded in — at most 90 s, or your Patience if that is longer. Started while standing on the path, there is no wait.', 'Waiting for the team to arrive - …'],
          [<strong className="text-white">Leaving a map</strong>, 'Through a gate, a teleport item, a teleport gate, or the jump back at the end of a one-map loop. Each client stops at its own exit until every teammate has reached an exit to the same map, then they all go.', 'Waiting for the team - …'],
          [<strong className="text-white">After a map change</strong>, 'Waits until everyone who came through the same exit has loaded in and appeared — at most 90 s, or your Patience if that is longer.', 'Waiting for the team to load in - …, then Arrived - going on (…) or The team is in - going on (…)'],
          [<strong className="text-white">A bloodshed point</strong>, 'Once every teammate has reached its own bloodshed point, everyone opens all the chests there freely. The status on FARM › Bloodshed says why a chest waits.', '—'],
        ]}
      />

      <Callout type="warning">
        <strong>One thing must match between the routes:</strong> which map each one leaves to. The
        exit itself may be a different gate. Bloodshed points are paired up in order &mdash; the first
        point of each route, then the second &mdash; so the same number per map works best. If one
        route has an extra point, the team does not gather there: that client only waits until the
        others have reached their exit, then opens its chests on its own, while the others wait for it
        at the exit.
      </Callout>

      <DocHeading>How Long It Waits</DocHeading>
      <DocTable
        headers={['Situation', 'How long']}
        rows={[
          ['A teammate is on the way: still walking, loading, at an earlier bloodshed point, or dead with a revive coming', 'At an exit or a bloodshed point: as long as it takes, so a long lap does not break the team up. At the start of a run and after a map change: at most 90 s, or your Patience if that is longer.'],
          ['The team cannot come together: a teammate waits at an exit to another map, or is dead and nobody revives them', 'Patience. The waiting reason then ends with alone in N s.'],
          ['A teammate’s route is not running', 'Not waited for at all.'],
          ['A teammate went ahead alone or fell behind a map', 'Not waited for. It rejoins as soon as it reaches the same map change as the rest.'],
        ]}
      />
      <Callout type="info">
        A dead teammate only holds the team if it is going to get up: its own{' '}
        <button onClick={() => navigate('auto-revive')} className={LINK}>Auto Revive</button> is on, or
        it has Accept resurrect offers on and a teammate nearby will offer a resurrect. Otherwise
        Patience starts counting.
      </Callout>

      <DocHeading>The TEAM Card</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<span className="text-gray-500">Off - this client works alone.</span>, 'Play as a party is off.'],
          [<strong className="text-white">This client</strong>, 'This client’s name and its place in the party, e.g. client 1  (slot 0).'],
          [<strong className="text-white">Doing</strong>, 'What this client is doing, with the number of map changes in this run: working, at a meeting point, ready to go, going through — or route is not running.'],
          [<>Where: <span className="text-amber-300">map or position unknown</span></>, 'Normal during loading.'],
          [<strong className="text-white">WAITING</strong>, 'What it is waiting at, why, and for how long, e.g. leaving the map: client 2 is on the way (12 s). Turns orange after 30 s.'],
          [<strong className="text-white">Teammates</strong>, 'A small block per teammate — see the next table.'],
          [<strong className="text-white">Resurrect</strong>, 'Only when a resurrect switch is on: Offers to me and Taken, My offers and Sent.'],
          [<strong className="text-white">Last map change</strong>, 'Waited for the team, Reason, When, and how many Map changes so far.'],
          [<strong className="text-white">Last time the team split</strong>, 'Where, Why, When and How many times the team went on without someone. It stays on the card, so you can read it after the fact.'],
        ]}
      />

      <p className="text-sm text-gray-400 mb-2 mt-8"><strong className="text-white">A teammate&rsquo;s rows</strong></p>
      <DocTable
        headers={['Row', 'Values']}
        rows={[
          ['The name, e.g. client 2', <>working · at a meeting point · ready to go · going through, with <em>@ exit</em> or <em>@ bloodshed point</em>. <span className="text-amber-300">route not running</span>, <span className="text-amber-300">dead</span> or <span className="text-amber-300">dead (reviving)</span> in orange.</>],
          ['where', <><span className="text-emerald-400">here, N m</span> · <span className="text-amber-300">same map, other instance</span> · <span className="text-amber-300">other map</span> · loading / position unknown · unknown - we do not know where we are</>],
          ['key farm', 'Only for clients taking part in Key Farm: A (quest) or killer, and its step.'],
          ['group bonus', <><span className="text-emerald-400">active</span> · <span className="text-amber-300">not active</span> · unknown — read from the game’s own group bonus.</>],
          ['map changes', <><span className="text-amber-300">behind (N vs our M)</span> or <span className="text-amber-300">ahead (N vs our M)</span> — only while both routes run and the counts differ: the team has split. See Quick Troubleshooting.</>],
          ['last heard', 'Shown when a teammate has been quiet for a moment. After 5 seconds of silence it drops off the list.'],
        ]}
      />

      <p className="text-sm text-gray-400 mb-2 mt-8"><strong className="text-white">Why it is waiting</strong></p>
      <DocTable
        headers={['The reason says', 'What it means']}
        rows={[
          ['client 2 is on the way', 'Still busy with its own route. Waits as long as it takes.'],
          ['… is loading', 'On a loading screen, or its position is not known yet.'],
          ['… is not on this map yet', 'At the start of a run: it has not arrived from town yet.'],
          ['… is coming through', 'It is already at the exit or going through.'],
          ['… is dead - waiting for the revive', 'Dead, but it will get up.'],
          ['… is still opening bloodshed chests · … is at an earlier bloodshed point', 'Behind at the bloodshed points of this map.'],
          ['… is at another meeting point (…) - alone in N s', 'Its route leaves to a different map. The routes do not match here.'],
          ['… is dead and nobody revives them - alone in N s', 'Turn on Auto Revive on that client, or Resurrect dead teammates on another.'],
          ['… is not ready - alone in N s', 'Held up for some other reason. Patience is counting.'],
          ['the group bonus is not active on … - opening anyway in N s', 'The ADVANCED group bonus option is waiting for that teammate.'],
          ['the group bonus of … cannot be read - opening anyway in N s', 'The bonus could not be read for that teammate, so it is not treated as active.'],
          ['waiting for everyone to be ready', 'Everyone is there and the ready signal is going round the team. Takes a moment.'],
          ['we have not loaded in yet', 'Only on AUTOPILOT › Play at the start of a run: this client itself is still loading.'],
        ]}
      />

      <DocHeading>Resurrecting Each Other</DocHeading>
      <DocTable
        headers={['Part', 'How it works']}
        rows={[
          ['Who offers', 'A living teammate with Resurrect dead teammates on, in the same instance, within 20 m of the dead one. If several could, only the first in slot order offers — exactly one stone per death.'],
          ['Accepting', 'The dead client with Accept resurrect offers on takes an offer meant for it after a short pause, and tries again every few seconds, up to 6 times.'],
          ['Your own Auto Revive', 'Waits up to about 6 s when a teammate close by is going to offer, and keeps waiting while an offer is being accepted. A teammate far away is not waited for.'],
        ]}
      />
      <DocTable
        headers={['Offers to me', 'My offers']}
        rows={[
          ['off · no offer', 'off · nobody is dead'],
          [<>offer just arrived · <span className="text-emerald-400">accepted</span> · accepted - waiting</>, <><span className="text-emerald-400">offered</span> · already offered for this death · a teammate before us offers</>],
          ['up again', 'the dead one is too far to reach · the dead one is on another map'],
          ['no answer - gave up', 'my position is unknown · I am dead too · the dead one has no actor id · the game thread is busy'],
        ]}
      />
      <Callout type="warning">
        <strong>Resurrect dead teammates spends a spiritstone every time.</strong> Turn it on only on
        characters that carry stones. Accepting an offer costs nothing.
      </Callout>

      <DocHeading>Party and Key Farm</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        <button onClick={() => navigate('key-farm')} className={LINK}>Key Farm</button> needs Play as a
        party on in every client &mdash; that is how its clients see each other. Key Farm runs its own
        timing, so while it runs the party stays out of the way: its path runs do not start or stop the
        other clients&rsquo; routes, the meeting points do not hold it, and a client in Key Farm does not
        start its loaded route on a party signal. Resurrecting each other still works.
      </p>

      <DocHeading>Quick Troubleshooting</DocHeading>
      <DocTable
        headers={['What you see', 'What it is']}
        rows={[
          ['Nobody right now.', 'Play as a party is off on the other clients, they run a different MortisEngine version, or they are on another PC. Every client must run the same version.'],
          ['Play as a party switches itself back off', 'Four clients are already in the party. A fifth one cannot join.'],
          ['Only one client starts', 'Start and stop together is off, Play as a party is off on the others, they have no route loaded, they were already running, or they are running Key Farm.'],
          ['map changes: behind / ahead', 'The team split up. Press STOP, then START — everyone begins from the same count again.'],
          ['It waits at an exit and never goes', 'A teammate is still on the way, and the reason names it. If that client is stuck, stop it: a client that is not running is not waited for.'],
          ['Last time the team split keeps saying is at another meeting point', 'The routes leave a map to different maps. Make them go to the same one.'],
          ['group bonus: unknown', 'The bonus could not be read. With the ADVANCED option on, chests open after Patience.'],
        ]}
      />
    </div>
  );
}

export default PageParty;
